import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { PARTNER_ADMIN_ROUTES, PARTNER_HOME, ROLES, type RoleKey } from "@/shared/lib/rbac";
import { Lock } from "lucide-react";
import {
  FORBIDDEN_MODULE_EXPIRED_PATH,
  FORBIDDEN_MODULE_NOT_ENABLED_PATH,
  lapsedModulesOf,
  moduleLockedNotice,
} from "@/shared/lib/forbiddenReason";
import { useComingSoonModules, useTenantModules } from "@/entities/dashboard";
import { PasswordChangeGate } from "@/features/auth/ui/PasswordChangeGate";

// Routes each role can access. Empty = all routes allowed; exceptions handled by denyRoutes below.
// 注:精细 sidebar 可见性 + 后端 hasRole 双重把关,本表只用作前端粗粒度兜底。
const roleRouteAccess: Record<RoleKey, string[]> = {
  PLATFORM_ADMIN: [],
  TENANT_ADMIN: [],
  TENANT_MANAGER: [
    "/",
    // 群页(发现流 /g/:cluster):首页横滑带的落点,漏配即经理点大类 403(测试机 0728 验收实锤,与 /ai-jizhe 同类)。
    "/g",
    "/dashboard",
    "/ai-kefu",
    "/ai-peilian",
    "/ai-haibao",
    "/ai-video",
    "/ai-digital-human",
    "/ai-explosive",
    // 官媒舱(R2;codex P1:module 门放行但本表缺 = MANAGER/STAFF 点即 403,与 W3.1 同类漏配)
    "/ai-jizhe",
    "/knowledge",
    // 人物库(packs W2):团队共用创作资产,租户三角色全员可用(后端同门)。漏配即 403(/g 同类)。
    "/characters",
    // 内容资产检索页(S2 默认口径 1):与官媒草稿租户共享(by-design)、爆款蓝图 STAFF 读
    // tenant 同口径;官媒 hub「查看全部」落 /works,漏配即 403(测试机存量疑点,本批修死)。
    "/works",
    "/products",
    "/channels",
    "/users",
    "/org-departments",
    "/tenant-audit",
    "/profile",
    "/ai-huoke", // 获客舱(地图获客 + 外链三卡)
    "/ai-website", // 建站舱(2026-09-22 转正:我的站点 / 模板 / 采集 / 编辑 / 域名)
    "/ai-promotion", // 推广舱过渡软链(1012):GEO 管理系统按租户开通,员工都能进
    // 官媒舱(媒体采购网资源检索,显示阶段):与获客舱同群;漏配即 MANAGER 点卡 403。
    "/ai-official-media",
    "/tenant-settings",
    "/industry-kb",
    // /tenant-quotas 已移出(0926 修复波 #10):那一页是平台工具(租户下拉 + 全部 /platform/** 接口),
    // 租户角色进去只会一屏 403,改由下方 platformIdentityOnlyRoutes 按真实身份放行。
    "/tenant-admin",
    "/geo-radar",
    "/biz-advisor",
    "/growth-cockpit",
    "/advisor-skills",
    // 经营增长 IA 重排(A2/A3):军师记忆/团队看板/派单回看 = MANAGER+ meta 页(无 module 二次门)。
    // 侧栏对 MANAGER 可见(roles=[ADMIN,MANAGER]);2026-07-30 起这三条都只是重定向到 /advisor?tab=board,
    // 白名单必须同步这三条,否则 MANAGER 点即 /403(与 W3.1 geo/biz 同类漏配)。
    "/advisor-memory",
    "/advisor-team-board",
    "/advisor-dispatches",
    // 团队任务(0817 走查实锤:经理点侧栏「团队任务」即 403,而后端 TaskController
    // 类级三角色全放行 —— 纯本表漏配,与 /g、/ai-jizhe、军师三项**同坑第四次**)。
    // ★第五次的保险不在这行注释,在 ProtectedRoute.navsync.test.tsx:侧栏 roles 含
    //   MANAGER 的每一条 url 都必须过本守卫,新增侧栏项漏配当场红。
    "/team-tasks",
    // 我的积分(2026-09-02 积分明细透明化批):自查页,侧栏 roles=[MANAGER,STAFF],
    // 与上面同坑 —— 白名单必须同步,否则经理点侧栏即 403。
    "/my-credits",
    // 帮助中心(2026-09-19 §21):新手指引 + 更新记录,全员可进。轨上有图标 = 白名单必须有它,
    // 否则「点帮助即 403」,与 /g、/ai-jizhe、军师三项、/team-tasks 同坑。
    "/help",
    // 即将上线页(0926 修复波 #10):侧栏「即将上线」组对租户全部角色露出(AppSidebar comingSoonItems,
    // scope=TENANT 即出),本表漏配 = 经理点开即 403。与 /g、/team-tasks 同坑。
    "/coming-soon",
    // 经营看板(2026-07-30 四合一):MANAGER 是主要用户(看盘/记忆/技能三 tab)。**新路由必须同步本表** ——
    // 漏配即经理点即 403,这坑已踩过三次(/g、/ai-jizhe、军师三项)。
    // 注:MANAGER 无「对话记录」tab(下方 deny 挡老路由 + 页内 visibleHubTabs 不给),两层各司其职。
    "/advisor",
  ],
  TENANT_STAFF: [
    "/",
    // 群页同上(员工也从首页横滑带进群)。
    "/g",
    "/dashboard",
    "/ai-kefu",
    "/ai-peilian",
    "/ai-haibao",
    "/ai-video",
    "/ai-digital-human",
    "/ai-explosive",
    // 官媒舱(R2;codex P1 同上)
    "/ai-jizhe",
    "/knowledge",
    // 人物库(packs W2):同上,STAFF 也建/用人物(库内治理面全员可见)。
    "/characters",
    // 内容资产检索页(S2 默认口径 1,同 MANAGER 注)。
    "/works",
    "/profile",
    // ADV-NAV-2(0818):军师满屏对话页 —— B1 路由注释明写「STAFF 有 agent_access 也能用,
    // 页内自门控」,但本表漏配 = STAFF 点「展开整页」即 403(0818 真机实锤;与 /g、/ai-jizhe、
    // 军师三项、/team-tasks 同坑第五次)。只放满屏对话页;经营看板 /advisor 及 meta 页仍不给
    // STAFF(deny 表 /advisor-conversations、/advisor-skills 原样),两层语义不变。
    "/advisor/chat",
    "/ai-huoke", // 获客舱(地图获客 + 外链三卡)
    "/ai-website", // 建站舱(2026-09-22 转正:我的站点 / 模板 / 采集 / 编辑 / 域名)
    "/ai-promotion", // 推广舱过渡软链(1012):GEO 管理系统按租户开通,员工都能进
    // 官媒舱(媒体采购网资源检索,显示阶段):同上 MANAGER 注。
    "/ai-official-media",
    // 我的积分(2026-09-02 积分明细透明化批):STAFF 自查页,同上 MANAGER 注。
    "/my-credits",
    // 帮助中心(2026-09-19 §21):员工最需要新手指引的那一档,漏配即点帮助 403。
    "/help",
    // 即将上线页(0926 修复波 #10):同上 MANAGER 注,侧栏对员工同样露出。
    "/coming-soon",
  ],
  MEMBER: [],
  // OEM 渠道商管理员(W3 C-G):只放渠道后台 + 个人中心 + 帮助 + 403,其余一律 403
  // (后端同口径:/api/v1/partner/** 与 /me/**,兜底档不放 PARTNER_ADMIN)。
  PARTNER_ADMIN: [...PARTNER_ADMIN_ROUTES],
};

// 路由 → 所需 module_key(物料 §6.4 设计意图)。无该 module(page==='none'/缺)→ 403。
// /profile 不设(个人中心是 authenticated 全员能力,不挂 module)。
const routeRequiredModule: Record<string, string> = {
  "/tenant-settings": "system_settings",
  // B2 #19(0927 K2 实测,基础版 QA 租户):客服舱 / 陪练舱此前没挂 module 门,未开通直访能进页壳、接口全 403。
  // 两者在 catalog 都是 gated=1;OEM H1 起 /me/permissions = RbacMatrix ∩ 租户权益(MeService.permissions(role, tenant)),
  // 所以 permissions.modules 对管理员也**不是恒有**——缺 = 公司没开通,落 /403?reason=module-not-enabled。
  "/ai-kefu": "ai_kefu",
  "/ai-peilian": "ai_peilian",
  // 1003 B8:视觉舱此前漏挂 —— 未开通租户能进卡墙 / 创建页 / 长图搭建器,填完提交才被后端
  // PosterService.requireModuleFor(ai_poster)拒。与其它舱同口径,未开通直访落「未开通」。
  "/ai-haibao": "ai_poster",
  "/ai-video": "ai_video",
  "/ai-digital-human": "ai_digital_human",
  "/ai-explosive": "ai_explosive_parse",
  "/ai-jizhe": "ai_reporter",
  // W3.1:增长引擎模块二次门。geo_radar/biz_advisor 在 catalog 都是 gated=1(OEM H1 起 permissions 按租户开通求交):
  // 租户未开通 → 缺;对 STAFF 也缺(RbacMatrix 不给)→ 403(叠 roleRouteAccess 白名单)。
  "/geo-radar": "geo_radar",
  "/biz-advisor": "biz_advisor",
  // 获客舱:全员可用(backend TENANT_STAFF+),二次门只挡「租户未开通模块」。
  "/ai-huoke": "ai_acquisition",
  // 建站舱(2026-09-22 转正):租户三级角色全员可进(后端 STAFF 只读),二次门只挡「租户未开通模块」。
  // roleRouteDeny.PLATFORM_ADMIN 不加,与 /ai-huoke 同口径(module 门本已挡平台超管)。
  "/ai-website": "ai_website",
  // 官媒舱(显示阶段):与获客舱同口径,二次门只挡「租户未开通模块」。
  "/ai-official-media": "ai_official_media",
};

// V27 (2026-05-20) — 删了 IMPERSONATION_UNLOCKABLE_PREFIXES 白名单 hack。
// 之前的设计:平台超管 deny 租户业务路径,代客时再单独把这堆"解锁"。
// 改后:统一用"视图角色"(effective role)决定路由 deny。
// - 非代客 PLATFORM_ADMIN → effective=PLATFORM_ADMIN, deny 租户路径(不变)
// - 代客 PLATFORM_ADMIN → effective=TENANT_ADMIN, deny=租户管理员的(不含租户业务页), 自然放行
// - 真 TENANT_ADMIN/MANAGER/STAFF → effective=自身, deny=自己的(不变)
const roleRouteDeny: Partial<Record<RoleKey, string[]>> = {
  // 平台超管:不应直访租户业务页(应走代客流程)
  PLATFORM_ADMIN: [
    "/ai-kefu",
    "/ai-peilian",
    "/ai-haibao",
    // 爆款情报 = 租户业务页(解析扣租户积分):非代客平台超管 deny(代客态 effectiveRole=
    // TENANT_ADMIN 正常放行)。module 门本已挡(RbacMatrix 不给 PLATFORM_ADMIN),此处纵深。
    "/ai-explosive",
    // 官媒舱同为租户业务页(出稿扣租户积分),同口径 deny。
    "/ai-jizhe",
    // 官媒舱(媒体采购网资源检索,显示阶段)同为租户业务页,同口径 deny。
    "/ai-official-media",
    "/knowledge",
    "/channels",
    "/my-billing",
    // 我的积分(2026-09-02 积分明细透明化批):同为租户自查业务页,与 /my-billing 同口径 deny
    // (代客态 effectiveRole=TENANT_ADMIN,不受此列表影响,正常放行)。
    "/my-credits",
    // S4:经营增长驾驶舱是租户业务 meta 页(无 module 二次门),与 geo/biz 靠 module 门挡平台超管不同,
    // 这里显式 deny 非代客平台超管直访(代客时 effectiveRole=TENANT_ADMIN,不受此列表影响,正常放行)。
    "/growth-cockpit",
    // ★deny 用的是**裸 startsWith**(无 "/" 边界,见下方 184 行)—— 故这一条 "/advisor" 同时覆盖
    // /advisor(经营看板)、/advisor-conversations、/advisor-skills、/advisor-memory、
    // /advisor-team-board、/advisor-dispatches。全是租户业务 meta 页,与 growth-cockpit 同口径:
    // 非代客平台超管不该直访(代客态 effectiveRole=TENANT_ADMIN,不受本表影响,正常放行)。
    "/advisor",
    // 组织管理 = 租户内建底座(部门树/成员归属);非代客平台超管直访会撞 /tenant/* 403,deny(平台反馈条1)。
    "/org-departments",
    "/tenant-audit",
    // 0926 修复波 #10:以下四条后端 SecurityConfig 只放租户三角色(/api/v1/tenant/**(biz-advisor)、
    // /api/v1/video/**、/api/v1/digital-human/**、/api/v1/characters/** 均 hasAnyRole(TENANT_*)),
    // 非代客平台超管直访 = 页壳能开、接口全 403。代客态 effectiveRole=TENANT_ADMIN 不受影响。
    "/biz-advisor",
    "/ai-video",
    "/ai-digital-human",
    "/characters",
    // OEM 渠道后台只给 PARTNER_ADMIN(后端 /api/v1/partner/** hasRole PARTNER_ADMIN,平台超管也 403)。
    PARTNER_HOME,
  ],
  TENANT_ADMIN: ["/permissions", "/tenants", "/platform", "/tenant-plans", "/admin", PARTNER_HOME],
  // /my-billing = TENANT_ADMIN-only(决策7/后端 D-1);MANAGER 直敲也 403(纵深,叠 sidebar 隐藏)。
  TENANT_MANAGER: [
    "/permissions",
    "/tenants",
    "/platform",
    "/tenant-plans",
    "/admin",
    "/my-billing",
    "/advisor-conversations",
    PARTNER_HOME,
  ],
  TENANT_STAFF: [
    "/permissions",
    "/tenants",
    "/platform",
    "/tenant-plans",
    "/admin",
    "/users",
    "/channels",
    "/industry-kb",
    "/my-billing",
    "/advisor-conversations",
    "/advisor-skills",
    PARTNER_HOME,
  ],
  // MEMBER 应走 wecom-h5 入口。主域 SPA 兜底全 deny(AuthContext 在 AUTO 模式下也会强制 logout,
  // 这里是双保险:即便 scope guard 失效,持 MEMBER token 也看不到任何管理台 UI)
  MEMBER: [
    "/dashboard",
    "/platform",
    "/admin",
    "/tenants",
    "/tenant-plans",
    "/permissions",
    "/users",
    "/industry-kb",
    "/my-billing",
    "/channels",
    "/ai-kefu",
    "/ai-peilian",
    "/ai-haibao",
    "/ai-video",
    "/ai-digital-human",
    "/ai-explosive",
    "/ai-jizhe",
    "/ai-official-media",
    "/knowledge",
    "/tenant-settings",
    "/tenant-quotas",
    "/advisor-skills",
    // 0926 修复波 #10:军师/经营诊断整组显式列出(不靠 "/advisor" 前缀碰巧覆盖 —— 裸 startsWith
    // 的覆盖关系一改就断,MEMBER 该走企微 H5 军师,不该在主域看到任何军师页)。
    "/advisor",
    "/advisor/chat",
    "/biz-advisor",
    "/advisor-dispatches",
    // 0926 修复波第三批:MEMBER 白名单为空 = 全放行,漏列的就能进。人物库(后端 /characters/** 只认
    // TENANT_*,进来一屏 403)与「即将上线」占位页(侧栏只对租户角色露出)补上。
    "/characters",
    "/coming-soon",
    PARTNER_HOME,
  ],
};

/**
 * 平台专属工具页:按**真实身份**(user.role)放行,不看视图角色 —— 平台超管代客时
 * effectiveRole=TENANT_ADMIN,但它仍是平台超管在操作,这类页要照常能进;真租户管理员则不行。
 *
 * /tenant-quotas(0926 修复波 #10 取舍):页面全部数据来自 /platform/**(租户下拉 /platform/tenants、
 * 套餐/订阅/策略/余额 /platform/billing/**、调额 /platform/tenants/{code}/credits/adjust),
 * 入口只有平台「租户详情 → 调整配额」。租户管理员进来只会看到一屏 403 的空壳,
 * 租户自己的套餐与余额看「我的套餐」(/my-billing)。故租户角色一律不放行。
 */
const platformIdentityOnlyRoutes: readonly string[] = ["/tenant-quotas"];

/** 租户侧视图角色:module 门拦下它们时,缺的是「租户没开通」而不是「角色不够」(见下方说明)。 */
const TENANT_VIEW_ROLES: readonly RoleKey[] = [
  ROLES.TENANT_ADMIN,
  ROLES.TENANT_MANAGER,
  ROLES.TENANT_STAFF,
];

/**
 * OEM H1 历史作品只读页:模块未开通 / 停用 / 过期(/me/permissions.historyModules)时仍可进的路由。
 * 只收「看作品」的页,且这两条线的新建在后端有开通闸(分身 doCreate / 新闻 createDraft·startTask 抛
 * MODULE_NOT_ENABLED),页内按钮误点也落不了单。海报 / 视频的详情页是编辑面(再来一张 / 改图 / 让它动起来)
 * 且后端新建无开通闸 → 不放,「作品」页用灯箱 / 播放器在列表内预览与下载。
 */
const HISTORY_READONLY_ROUTES: RegExp[] = [
  /^\/ai-digital-human\/tasks\/[^/]+$/,
  /^\/ai-jizhe\/preview\/[^/]+$/,
];

function isHistoryReadonlyRoute(pathname: string): boolean {
  return HISTORY_READONLY_ROUTES.some((re) => re.test(pathname));
}

/** 只读横幅:历史作品可看、可下载;新建 / 消耗积分的动作不可用。 */
function ModuleHistoryReadonly({
  notice,
  children,
}: {
  /** OEM 第二批 A7:被服务商暂停 / 未开通 两种说法(moduleLockedNotice)。 */
  notice: string;
  children: React.ReactNode;
}) {
  return (
    <>
      <div
        role="status"
        data-testid="module-history-readonly"
        className="mb-4 flex items-center gap-2 rounded-lg border border-warning/40 bg-warning/10 px-3 py-2 text-sm text-foreground"
      >
        <Lock className="h-4 w-4 shrink-0 text-warning" />
        <span>{notice}。历史作品仍可查看、下载。</span>
      </div>
      {children}
    </>
  );
}

function matchesRoute(pathname: string, p: string) {
  return pathname === p || pathname.startsWith(p + "/");
}

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated, user, permissions, passwordChangeRequired } = useAuth();
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // 0930 首登强制改密:会话必须先改密时,任何受保护路由都只渲染改密闸(刷新 / 直敲 URL 都绕不过)。
  // 后端同时对这种会话只放行改密 / 登出 / 读 /me,前端这层只是不让用户看到一屏 403。
  if (passwordChangeRequired) {
    return <PasswordChangeGate />;
  }

  if (user) {
    // V27 (2026-05-20) — 视图角色:代客时是 TENANT_ADMIN(后端 /me/permissions 切),
    // 非代客时跟真实身份一致。所有路由 deny / allow 检查都按视图角色,
    // 不再用 user.role 硬编码 + IMPERSONATION_UNLOCKABLE_PREFIXES 半成品白名单。
    const effectiveRole = ((permissions?.role as RoleKey | undefined) ?? user.role) as RoleKey;

    // OEM 渠道管理员没有租户首页:落到 / 或 /dashboard(登录默认落点、Logo 点击、老书签)→ 渠道后台首页,
    // 而不是一屏 403。
    if (
      effectiveRole === ROLES.PARTNER_ADMIN &&
      (location.pathname === "/" || location.pathname === "/dashboard")
    ) {
      return <Navigate to={PARTNER_HOME} replace />;
    }

    if (
      platformIdentityOnlyRoutes.some((p) => matchesRoute(location.pathname, p)) &&
      user.role !== ROLES.PLATFORM_ADMIN
    ) {
      return <Navigate to="/403" replace />;
    }

    const denied = roleRouteDeny[effectiveRole] ?? [];
    if (denied.some((p) => location.pathname.startsWith(p))) {
      return <Navigate to="/403" replace />;
    }
    const allowed = roleRouteAccess[effectiveRole] ?? [];
    // 用前缀匹配,与 deny 对齐 — 否则 /ai-haibao 在 allow 但 /ai-haibao/tasks/42 被踢 403
    if (
      allowed.length > 0 &&
      !allowed.some((p) => location.pathname === p || location.pathname.startsWith(p + "/"))
    ) {
      return <Navigate to="/403" replace />;
    }

    // module 门禁:路由要求的 module 必须可见(page !== 'none')。permissions 未加载时放行(避免误杀),
    // 加载后无该 module 则 403 — 修掉「菜单藏却 URL 可进」的不一致(如 /tenant-settings 需 system_settings)。
    const reqEntry = Object.entries(routeRequiredModule).find(([p]) =>
      matchesRoute(location.pathname, p)
    );
    if (reqEntry && permissions) {
      const mod = permissions.modules?.[reqEntry[1]];
      if (!mod || mod.page === "none") {
        // 0926 修复波 #10:租户视图角色能走到这里,说明角色门(白名单/deny)已放行,且后端
        // RbacMatrix 给这些角色的矩阵里本就含全部 routeRequiredModule 模块 —— 缺失只可能是
        // MeService 按租户权益求交把它剔掉了 = 公司没开通。此时说「你的角色无权访问」是错话。
        // 平台超管(非代客)缺模块是真的角色不对口,仍走通用 403。
        const notEnabled = TENANT_VIEW_ROLES.includes(effectiveRole);
        if (!notEnabled) return <Navigate to="/403" replace />;
        // 1002:客服舱停用(暂停 / 过期)→ 客服页「只读 + 人工处理」照常进(页内自带停用横幅、隐藏 AI 入口)。
        // 从没拥有过客服舱的租户后端仍 403 MODULE_NOT_ENABLED(reason=NOT_OWNED),页内据此转去「未开通」。
        if (
          reqEntry[1] === "ai_kefu" &&
          (permissions.everOwnedModules ?? permissions.historyModules)?.includes("ai_kefu") &&
          matchesRoute(location.pathname, "/ai-kefu")
        ) {
          return <>{children}</>;
        }
        // OEM H1:未开通 / 停用 / 过期的模块,历史作品的只读页照常可看(带只读横幅)。
        if (
          permissions.historyModules?.includes(reqEntry[1]) &&
          isHistoryReadonlyRoute(location.pathname)
        ) {
          return (
            <ModuleHistoryReadonly
              notice={moduleLockedNotice(reqEntry[1], permissions.pausedModules)}
            >
              {children}
            </ModuleHistoryReadonly>
          );
        }
        // 0926 建站舱暂停开放:后端把整体暂停(catalog coming_soon)的舱从 modules 里剔掉,缺失的
        // 另一种原因是「即将上线」而不是「公司没开通」—— 这时去「即将上线」页,不说「未开通」。
        return <TenantModuleMissingRedirect capability={reqEntry[1]} />;
      }
      // OEM G2:租户视图下再对一次 /me/modules(按权益出卡,status=disabled = 没开通)。
      // OEM H1 起后端 /me/permissions 已与租户权益求交(MeService.permissions(role, tenant)),未开通的舱
      // 在上面 module 缺失分支就被拦下;这道门保留为纵深防御 —— permissions 来自 localStorage 缓存,
      // 后台刷新(回焦 / 5 分钟)之前可能陈旧,/me/modules 先刷到「已关闭」时以更严的为准。
      if (TENANT_VIEW_ROLES.includes(effectiveRole)) {
        return (
          <TenantModuleEntitlementGate capability={reqEntry[1]}>
            {children}
          </TenantModuleEntitlementGate>
        );
      }
    }
  }

  return <>{children}</>;
}

/**
 * 路由所需模块的「租户开通」门(OEM G2)。数据 = /me/modules(与工作台卡墙同一份缓存)。
 * - 清单里有该模块且 status 非 active → 公司没开通 → 与 module 缺失同一去向(即将上线 / 未开通 403);
 * - 清单里没有该模块(非计费舱,如 system_settings / geo_radar)、还在加载、拉取失败 → 放行,
 *   不为一次探测白屏,也不误杀(与「permissions 未加载时放行」同口径)。
 */
function TenantModuleEntitlementGate({
  capability,
  children,
}: {
  capability: string;
  children: React.ReactNode;
}) {
  const { data } = useTenantModules();
  const card = data?.find((m) => m.code === capability);
  if (card && card.status != null && card.status !== "active") {
    return <TenantModuleMissingRedirect capability={capability} />;
  }
  return <>{children}</>;
}

/**
 * 租户视图下路由要的 module 缺失时的去向:该能力在「即将上线」清单里(catalog coming_soon,
 * 如 0926 暂停开放的建站舱)→ /coming-soon/:capability;否则 → 「未开通」403。
 * 清单还在加载时先不跳(避免先闪一下「未开通」);清单拉取失败按「未开通」处理。
 */
function TenantModuleMissingRedirect({ capability }: { capability: string }) {
  const { data, isLoading } = useComingSoonModules();
  const { permissions } = useAuth();
  // 1005:拥有过、现在关着、没被暂停 = 因到期(套餐 / 增值舱)关闭 —— 落「已到期」页,不说「还没开通」
  if (lapsedModulesOf(permissions).includes(capability)) {
    return <Navigate to={FORBIDDEN_MODULE_EXPIRED_PATH} replace />;
  }
  if (isLoading) return null;
  const comingSoon = (data ?? []).some((m) => m.capability === capability);
  return (
    <Navigate
      to={comingSoon ? `/coming-soon/${capability}` : FORBIDDEN_MODULE_NOT_ENABLED_PATH}
      replace
    />
  );
}
